<template>
  <div class="ve-split">
    <!-- Left: list of objectives -->
    <div class="ve-list-panel">
      <div class="ve-list-header">
        <span class="ve-list-title">{{ objectives.length }} objective{{ objectives.length === 1 ? '' : 's' }}</span>
        <button
          type="button"
          class="ve-add-btn"
          :class="{ 've-add-btn--active': newObjectiveActive && editingObjectiveIndex === null }"
          @click="startNewObjective"
        >+ Add</button>
      </div>
      <div class="ve-list-body">
        <div v-if="objectives.length === 0" class="ve-list-empty">No objectives yet</div>
        <div
          v-for="(obj, oi) in objectives"
          :key="oi"
          class="ve-list-item"
          :class="{
            've-list-item--selected': newObjectiveActive && editingObjectiveIndex === oi,
            've-list-item--disabled': newObjectiveActive && editingObjectiveIndex !== oi
          }"
          :title="newObjectiveActive && editingObjectiveIndex !== oi ? 'Finish editing before selecting another' : ''"
          @click="editObjective(oi)"
        >
          <span class="ve-list-name">{{ obj.name }}</span>
          <span style="font-size:0.72rem;color:#8b949e;flex-shrink:0;margin-right:0.25rem">{{ obj.tokens.length }}🪙</span>
          <button type="button" class="ve-list-remove" @click.stop="removeObjective(oi)">✕</button>
        </div>
      </div>
    </div>

    <!-- Right: form (add/edit) or placeholder -->
    <div class="ve-detail-panel">
      <!-- Inactive placeholder -->
      <div v-if="!newObjectiveActive" style="padding:3rem 1rem; text-align:center">
        <div style="font-size:2.5rem; margin-bottom:0.75rem">🎯</div>
        <p style="color:#8b949e; font-size:0.88rem">
          Click <strong style="color:#e6edf3">+ Add</strong> to create a new objective,<br>
          or click an existing objective to edit it.
        </p>
      </div>

      <!-- Active form: add or edit -->
      <div v-else class="new-objective-form">
        <div class="field-row" style="max-width:380px; margin-bottom:1rem">
          <label class="field-label">Objective Name</label>
          <input v-model="newObjectiveName" type="text" class="field-input" placeholder="e.g. Capture the Hill" />
        </div>

        <!-- Token palette -->
        <p class="field-label">Drag tokens onto the battlefield</p>
        <p v-if="availableTokenSkins.length === 0" class="field-desc" style="margin-top:0.3rem">
          No tokens selected — select tokens first in the Tokens section.
        </p>
        <div v-else class="obj-token-palette">
          <img
            v-for="skin in availableTokenSkins"
            :key="skin.id"
            :src="skin.thumbnailUrl"
            :alt="skin.name"
            :title="skin.name"
            class="obj-token-thumb"
            draggable="true"
            @dragstart="onObjectiveTokenDragStart(skin, $event)"
          />
        </div>

        <!-- Battlefield -->
        <div class="obj-battlefield-wrap">
          <div
            ref="battlefieldRect"
            class="obj-battlefield"
            :style="battlefieldRectStyle"
            @dragover.prevent="onObjectiveBfDragover($event)"
            @dragleave="onObjectiveBfDragLeave($event)"
            @drop.prevent="onObjectiveBfDrop($event)"
          >
            <div v-if="newObjectiveTokens.length === 0 && !objectiveDragPos" class="obj-bf-placeholder">
              Drop tokens here
            </div>
            <div
              v-if="objectiveDragPos"
              class="obj-coord-tooltip"
              :style="{ left: objectiveDragPos.labelLeft + 'px', top: objectiveDragPos.labelTop + 'px' }"
            >
              X:&nbsp;{{ objectiveDragPos.x.toFixed(1) }}"&nbsp;&nbsp;Y:&nbsp;{{ objectiveDragPos.y.toFixed(1) }}"
            </div>
            <div
              v-for="(t, ti) in newObjectiveTokens"
              :key="ti"
              class="obj-placed-token"
              :class="{ 'obj-placed-token--selected': selectedObjectiveTokenIndex === ti }"
              :style="{ left: ((t.x + bfWidth / 2) / bfWidth * 100) + '%', top: ((t.y + bfHeight / 2) / bfHeight * 100) + '%' }"
              :title="t.name"
              draggable="true"
              @dragstart.stop="onPlacedTokenDragStart(ti, $event)"
              @click.stop="selectObjectiveToken(ti)"
            >
              <img :src="t.thumbnailUrl" :alt="t.name" class="obj-placed-img" />
              <div class="obj-placed-coords">{{ t.x.toFixed(1) }}" {{ t.y.toFixed(1) }}"</div>
            </div>
          </div>
          <p class="field-desc" style="margin-top:0.35rem; text-align:center">
            Battlefield: {{ bfWidth }}" × {{ bfHeight }}"
            &nbsp;·&nbsp; {{ newObjectiveTokens.length }} token{{ newObjectiveTokens.length === 1 ? '' : 's' }} placed
            &nbsp;·&nbsp; Click to select · drag to reposition
          </p>
        </div>

        <!-- Selected placed token panel -->
        <div
          v-if="selectedObjectiveTokenIndex !== null && selectedObjectiveTokenIndex < newObjectiveTokens.length"
          class="obj-selected-panel"
        >
          <span class="obj-selected-name">{{ newObjectiveTokens[selectedObjectiveTokenIndex].name }}</span>
          <div class="obj-selected-fields">
            <label class="field-label">X"</label>
            <input
              type="number" step="0.1" class="field-input field-input-narrow"
              :value="newObjectiveTokens[selectedObjectiveTokenIndex].x"
              @change="$set(newObjectiveTokens[selectedObjectiveTokenIndex], 'x', parseFloat($event.target.value) || 0)"
            />
            <label class="field-label">Y"</label>
            <input
              type="number" step="0.1" class="field-input field-input-narrow"
              :value="newObjectiveTokens[selectedObjectiveTokenIndex].y"
              @change="$set(newObjectiveTokens[selectedObjectiveTokenIndex], 'y', parseFloat($event.target.value) || 0)"
            />
            <button
              class="obj-remove-token-btn" type="button" title="Remove token"
              @click="newObjectiveTokens.splice(selectedObjectiveTokenIndex, 1); selectedObjectiveTokenIndex = null"
            >&times;</button>
          </div>
        </div>

        <div style="display:flex; gap:0.75rem; margin-top:1rem; align-items:center">
          <button class="btn btn-secondary" type="button" @click="cancelNewObjective">Cancel</button>
          <button
            class="btn btn-primary" type="button"
            @click="addObjective"
            :disabled="!newObjectiveName.trim()"
          >
            {{ editingObjectiveIndex !== null ? 'Save objective' : 'Add objective' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ObjectivesEditor',

  props: {
    value: { type: Array, default: () => [] },
    availableTokenSkins: { type: Array, default: () => [] },
    battlefieldWidth: { type: Number, default: 72 },
    battlefieldHeight: { type: Number, default: 48 },
  },

  data() {
    return {
      objectives: [],
      newObjectiveActive: false,
      newObjectiveName: '',
      newObjectiveTokens: [],
      objectiveDragPos: null,
      selectedObjectiveTokenIndex: null,
      editingObjectiveIndex: null,
      editingObjectiveBackup: null,
    };
  },

  computed: {
    bfWidth() { return this.battlefieldWidth || 72; },
    bfHeight() { return this.battlefieldHeight || 48; },
    battlefieldRectStyle() {
      return { aspectRatio: `${this.bfWidth} / ${this.bfHeight}` };
    },
  },

  mounted() {
    if (this.value && this.value.length > 0) {
      this.initFromValue(this.value);
    }
  },

  watch: {
    value(newVal) {
      if (!this.newObjectiveActive) {
        this.initFromValue(newVal);
      }
    },
    availableTokenSkins(newSkins) {
      if (newSkins.length > 0 && this.value && this.value.length > 0) {
        this.initFromValue(this.value);
      }
    },
  },

  methods: {
    initFromValue(json) {
      this.objectives = (json || []).map(obj => ({
        name: obj.Name,
        tokens: (obj.Tokens || []).map(t => {
          const skin = this.availableTokenSkins.find(s => String(s.id) === String(t.Id));
          return {
            id: String(t.Id),
            name: skin ? skin.name : String(t.Id),
            thumbnailUrl: skin ? skin.thumbnailUrl : '',
            x: t.X || 0,
            y: t.Y || 0,
          };
        }),
      }));
    },

    startNewObjective() {
      if (this.newObjectiveActive) return;
      this.newObjectiveActive = true;
      this.newObjectiveName = '';
      this.newObjectiveTokens = [];
      this.objectiveDragPos = null;
      this.selectedObjectiveTokenIndex = null;
      this.editingObjectiveIndex = null;
      this.editingObjectiveBackup = null;
    },

    cancelNewObjective() {
      if (this.editingObjectiveIndex !== null) {
        this.objectives.splice(this.editingObjectiveIndex, 0, this.editingObjectiveBackup);
        this.editingObjectiveIndex = null;
        this.editingObjectiveBackup = null;
      }
      this.newObjectiveActive = false;
      this.newObjectiveName = '';
      this.newObjectiveTokens = [];
      this.objectiveDragPos = null;
      this.selectedObjectiveTokenIndex = null;
    },

    addObjective() {
      const name = this.newObjectiveName.trim();
      if (!name) return;
      const saved = { name, tokens: this.newObjectiveTokens.slice() };
      if (this.editingObjectiveIndex !== null) {
        this.objectives.splice(this.editingObjectiveIndex, 0, saved);
        this.editingObjectiveIndex = null;
        this.editingObjectiveBackup = null;
      } else {
        this.objectives.push(saved);
      }
      this.newObjectiveActive = false;
      this.newObjectiveName = '';
      this.newObjectiveTokens = [];
      this.objectiveDragPos = null;
      this.selectedObjectiveTokenIndex = null;
      this.$emit('input', this.buildObjectivesJson());
    },

    editObjective(oi) {
      if (this.newObjectiveActive) return;
      const obj = this.objectives[oi];
      this.editingObjectiveIndex = oi;
      this.editingObjectiveBackup = { name: obj.name, tokens: obj.tokens.map(t => ({ ...t })) };
      this.objectives.splice(oi, 1);
      this.newObjectiveName = obj.name;
      this.newObjectiveTokens = obj.tokens.map(t => ({ ...t }));
      this.newObjectiveActive = true;
      this.selectedObjectiveTokenIndex = null;
      this.objectiveDragPos = null;
    },

    removeObjective(oi) {
      this.objectives.splice(oi, 1);
      this.$emit('input', this.buildObjectivesJson());
    },

    selectObjectiveToken(ti) {
      this.selectedObjectiveTokenIndex = this.selectedObjectiveTokenIndex === ti ? null : ti;
    },

    onObjectiveTokenDragStart(skin, event) {
      event.dataTransfer.setData('text/plain', 'skin:' + String(skin.id));
      event.dataTransfer.effectAllowed = 'copy';
    },

    onPlacedTokenDragStart(ti, event) {
      event.dataTransfer.setData('text/plain', 'placed:' + ti);
      event.dataTransfer.effectAllowed = 'move';
      this.selectedObjectiveTokenIndex = null;
    },

    onObjectiveBfDragover(event) {
      const el = this.$refs.battlefieldRect;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const pxX = event.clientX - rect.left;
      const pxY = event.clientY - rect.top;
      const bw = this.bfWidth, bh = this.bfHeight;
      const x = Math.max(-bw / 2, Math.min(bw / 2, (pxX / rect.width) * bw - bw / 2));
      const y = Math.max(-bh / 2, Math.min(bh / 2, (pxY / rect.height) * bh - bh / 2));
      this.objectiveDragPos = {
        x, y,
        labelLeft: Math.min(pxX + 14, rect.width - 130),
        labelTop: Math.max(4, pxY - 30),
      };
    },

    onObjectiveBfDragLeave(event) {
      const el = this.$refs.battlefieldRect;
      if (el && !el.contains(event.relatedTarget)) {
        this.objectiveDragPos = null;
      }
    },

    onObjectiveBfDrop(event) {
      const data = event.dataTransfer.getData('text/plain');
      if (!this.objectiveDragPos) return;
      if (data.startsWith('placed:')) {
        const ti = parseInt(data.split(':')[1], 10);
        if (ti >= 0 && ti < this.newObjectiveTokens.length) {
          this.$set(this.newObjectiveTokens[ti], 'x', parseFloat(this.objectiveDragPos.x.toFixed(1)));
          this.$set(this.newObjectiveTokens[ti], 'y', parseFloat(this.objectiveDragPos.y.toFixed(1)));
        }
      } else {
        const skinId = data.startsWith('skin:') ? data.slice(5) : data;
        const skin = this.availableTokenSkins.find(s => String(s.id) === skinId);
        if (!skin) return;
        this.newObjectiveTokens.push({
          id: String(skin.id),
          name: skin.name,
          thumbnailUrl: skin.thumbnailUrl,
          x: parseFloat(this.objectiveDragPos.x.toFixed(1)),
          y: parseFloat(this.objectiveDragPos.y.toFixed(1)),
        });
      }
      this.objectiveDragPos = null;
    },

    buildObjectivesJson() {
      return this.objectives.map(obj => ({
        Name: obj.name,
        Tokens: obj.tokens.map(t => ({ Id: t.id, X: t.x, Y: t.y })),
      }));
    },
  },
};
</script>

<style>
.new-objective-form { background: transparent; }
.obj-token-palette { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-top: 0.4rem; margin-bottom: 0.75rem; }
.obj-token-thumb { width: 64px; height: 64px; object-fit: contain; border: 1px solid #30363d; border-radius: 6px; background: #21262d; cursor: grab; padding: 2px; transition: border-color 0.15s; }
.obj-token-thumb:hover { border-color: #58a6ff; }
.obj-token-thumb:active { cursor: grabbing; }
.obj-battlefield-wrap { margin-top: 0.75rem; display: flex; flex-direction: column; align-items: center; }
.obj-battlefield { position: relative; width: 100%; max-width: 600px; background: rgb(17, 30, 36); border: 3px solid #ffffff; border-radius: 4px; overflow: hidden; cursor: crosshair; }
.obj-bf-placeholder { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; font-size: 0.85rem; color: #3a5a3a; pointer-events: none; user-select: none; }
.obj-coord-tooltip { position: absolute; background: rgba(0,0,0,0.78); color: #fff; font-size: 0.75rem; padding: 3px 8px; border-radius: 4px; pointer-events: none; white-space: nowrap; z-index: 10; }
.obj-placed-token { position: absolute; transform: translate(-50%, -50%); display: flex; flex-direction: column; align-items: center; cursor: pointer; z-index: 5; user-select: none; }
.obj-placed-img { width: 32px; height: 32px; object-fit: contain; display: block; border-radius: 4px; border: 2px solid transparent; transition: border-color 0.15s; pointer-events: none; }
.obj-placed-token:hover .obj-placed-img { border-color: #58a6ff; }
.obj-placed-token--selected .obj-placed-img { border-color: #58a6ff; box-shadow: 0 0 0 2px rgba(88,166,255,0.4); }
.obj-placed-coords { font-size: 0.6rem; color: #fff; background: rgba(0,0,0,0.7); border-radius: 3px; padding: 1px 4px; white-space: nowrap; margin-top: 2px; pointer-events: none; line-height: 1.4; }
.obj-selected-panel { margin-top: 0.75rem; background: #1c2942; border: 1px solid #58a6ff; border-radius: 6px; padding: 0.55rem 0.85rem; display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap; }
.obj-selected-name { font-size: 0.88rem; color: #e6edf3; font-weight: 500; flex: 1; min-width: 80px; }
.obj-selected-fields { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
.obj-remove-token-btn { width: 32px; height: 32px; min-width: 32px; display: flex; align-items: center; justify-content: center; background: #c0392b; border: none; border-radius: 4px; color: #fff; font-size: 1.1rem; line-height: 1; cursor: pointer; flex-shrink: 0; transition: background 0.15s; padding: 0; }
.obj-remove-token-btn:hover { background: #e74c3c; }
</style>
